import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col, Card } from 'react-bootstrap';
import UserListItem from './UserListItem/UserListItem';
import UserModal from '../../Modals/UserModal/UserModal';
import LoadingOverlay from '../../common/LoadingOverlay';
import Pagination from '../../common/Pagination';
import { fetchPartnerUsers } from '../../../actions';
import { useTranslation } from 'react-i18next';
import handleClientSideSearch from '../../../utils/handleClientSideSearch';
import handleClientSideSorting from '../../../utils/handleClientSideSorting';
import api from '../../../api';
import { toastrAdd } from '../../../actions';

import usePagination from '../../../hooks/usePagination';

function UsersList() {
	const dispatch = useDispatch();
	const partnerID = localStorage.getItem('partnerID');
	const { users, usersLoading, /* usersError */ } = useSelector(({ partner }) => partner);
	const { t } = useTranslation();
	const [showUserModal, setShowUserModal] = useState(false);
	const [limit, setLimit] = useState(10);
	const [search, setSearch] = useState(null);
	const [sorter, setSorter] = useState(null);
	let filteredUsers = [...users];
	const sortingOptions = [
        { value: '{ "column": "firstNames", "direction": "asc", "type": "string" }', text: `${t('general.first_name')} (A-Z)` },
		{ value: '{ "column": "firstNames", "direction": "desc", "type": "string" }', text: `${t('general.first_name')} (Z-A)` },
		{ value: '{ "column": "lastName", "direction": "asc", "type": "string" }', text: `${t('general.last_name')} (A-Z)` },
        { value: '{ "column": "lastName", "direction": "desc", "type": "string" }', text: `${t('general.last_name')} (Z-A)` },
        { value: '{ "column": "email", "direction": "asc", "type": "string" }', text: `${t('general.email')} (A-Z)` },
        { value: '{ "column": "email", "direction": "desc", "type": "string" }', text: `${t('general.email')} (Z-A)` },
    ];

	const fetchUsers = useCallback(() => {
		dispatch(fetchPartnerUsers(partnerID));
	}, [dispatch, partnerID]);

	useEffect(() => {
		fetchUsers();
	}, [fetchUsers]);

	const deleteUser = (user) => {
		api.user.deletePartnerUser(user)
			.then(fetchUsers)
			.catch((error) => {
				fetchUsers();
				dispatch(toastrAdd({
					code: 0,
					details: `${t('general.details')}: ${error.firstError.title} - ${error.firstError.details}`,
					title: t('errors.general_delete_error_title'),
					type: 'error'
				}));
			});
	};
  
	if (search) {
		filteredUsers = handleClientSideSearch(filteredUsers, search);
	}

	if (sorter) {
		filteredUsers = handleClientSideSorting(filteredUsers, sorter)
	}
  
	const {
		setPage,
		pageCount,
		currentPage,
		currentData,
	} = usePagination(filteredUsers, limit);

	const currentUsers = currentData();

	const createNewUser = () => setShowUserModal(true);
	const closeUserModal = () => setShowUserModal(false);
	const handlePageSize = ({ target }) => setLimit(target.value);
	const handleSearch = ({ target }) => setSearch(target.value || null);
	const handleSorting = ({ target }) => setSorter(JSON.parse(target.value));
	const paginate = ({ selected }) => setPage(selected + 1);

	return (
		<Row>
			<Col md={12}>
				<h3>{t('general.users')}</h3>
				<Card>
					<Card.Header>
							{t('users.browse_users')}
							<span className="btn btn-link btn-sm float-right" style={{ fontWeight: 'bold' }} size="sm" onClick={createNewUser}>{t('general.create_new')}</span>

					</Card.Header>

					<div className="container DataListInputs">
                        <div className="row justify-content align-items-center">
                            <div className="col col-sm-auto">
                                <select
                                    className="form-control form-control-sm"
                                    defaultValue={limit}
                                    onChange={handlePageSize}
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                </select>
                            </div>
                            <div className="col col-sm-auto">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder={t('general.search')}
                                    onChange={handleSearch}
                                />
                            </div>
                            <div className="col col-sm-auto">
                                <select
                                    className="form-control form-control-sm"
                                    defaultValue="empty"
                                    onChange={handleSorting}
                                >
									<option disabled value="empty"></option>
                                    {sortingOptions.map((o, i) => (
                                        <option
                                            key={i}
                                            value={o.value}
                                        >
                                            {o.text}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

					<div className="DataList">
						{currentUsers.map(user => (
							<UserListItem
								key={user.userID}
								user={user}
								deleteUser={deleteUser}
								reload={fetchUsers}
							/>
						))}
						{usersLoading ?
							<LoadingOverlay />
						: null}
					</div>

					{users.length ?
                        <Card.Footer>
                            <div className="pull-right">
                                <Pagination
									forcePage={currentPage}
                                    pageCount={pageCount}
                                    marginPagesDisplayed={2}
                                    pageRangeDisplayed={5}
                                    onPageChange={paginate}
                                />
                            </div>
                        </Card.Footer>
					: null}

                    {!users.length ?
                        <Card.Body>
                            {t('users.no_users')}
                        </Card.Body>
					: null}
				</Card>
			</Col>

			{showUserModal ?
				<UserModal
					show={showUserModal}
					user={{}}
					onClose={closeUserModal}
					callback={fetchUsers}
					users={users}
				/>
			: null}
		</Row>
	);
}

export default UsersList;
